/* ============================================================
   PROMPT COMPOSER  (/workspace-v3.9)  — SESAAT Studio v3.9
   ============================================================
   Menggantikan SELURUH bahagian atas panel kiri Extension dengan
   satu kotak input. Yang diserap:

     panel kiri lama                →  di sini
     ─────────────────────────────────────────────────────────
     kotak "add product data"       →  input utama
     butang Add Products            →  butang "+ Produk"; produk
                                       yang dipilih jadi thumbnail
                                       berpalang dalam kotak input
     kad AI AVATAR + dropdown       →  chip Avatar yang memaparkan
                                       muka; klik = galeri petak muka
                                       (Tiada · Default L/P · avatar
                                       simpanan · slot kosong)
     MODE PRO + suis Auto           →  chip Gaya
     grid 6 MODULES                 →  chip Formula
     bilangan set                   →  chip Set
     kuota harian                   →  pil kredit
     butang jana                    →  butang Generate

   Bentuk diambil daripada corak "prompt input" yang sudah biasa
   digunakan pengguna (ChatGPT, BatchBot, penjana imej): satu
   kotak, lampiran di atas, chip tetapan di bawah, tindakan utama
   di kanan bawah. Pengguna tidak perlu belajar susun atur baharu.

   ⭐ KENAPA LAMPIRAN, BUKAN SATU PRODUK: bilangan produk yang
   dilampirkan tidak terhad, dan SATU tekanan Generate menjana
   kesemuanya. Ini menjaga peraturan bulk-first — tiada langsung
   laluan yang memaksa pengguna menjana satu demi satu.

   ⛔ SENGAJA TIADA DI SINI: Video mode, Image mode, In project,
      Collection, jadual TikTok. Semua itu tetapan LARIAN
      (automation), bukan tetapan JANAAN. Tempatnya di Panel
      larian; ikon pelaras di hujung baris chip membukanya.

   ⚠️ PROTOTAIP — butang Generate tidak menjana apa-apa dan tiada
      sambungan Firestore.
   ============================================================ */

import { useEffect, useRef, useState } from 'react';
import {
    Plus, X, Check, ChevronDown, Zap, Search, Lock, Info,
    UserX, Pencil, Wand2, Upload, Package, ShoppingCart, Minus,
    LayoutGrid, Rows3, Tag,
} from 'lucide-react';

import { Badge, Btn, Field, Segmented } from '../design/ui';
import { useT, useLang } from '../i18n';
import SusunMenuV39 from './SusunMenuV39';
import PilihV39 from './PilihV39';

/* ⭐ DUA BAHASA — fail ini v3.9 SAHAJA (dirender di belakang suis
   `uiV39` dalam App.jsx dan pada halaman prototaip WorkspaceV39View),
   jadi setiap komponen di sini memanggil `useT()` terus. Tiada rupa
   lama yang mengimportnya, jadi tiada risiko ayat English terselit
   ke dalam skrin pelanggan yang masih pada rupa lama.

   ⚠️ PEMALAR ARAS-MODUL TIDAK BOLEH memanggil hook. Sebab itu senarai
      tetap di bawah (avatar, gaya, tab, susunan, sebab PENDING)
      menyimpan KUNCI kamus, bukan ayat siap — ayat dibina pada masa
      render, di dalam komponen. */

const cx = (...parts) => parts.filter(Boolean).join(' ');

/* Adakah tekanan itu mendarat di atas sesuatu yang TERAPUNG di hadapan
   halaman — modal, menu chip, panel pemilih produk?

   ⚠️ Semakan mudah "ada nenek moyang position:fixed" TIDAK boleh dipakai
      di sini: StudioShell membungkus SELURUH halaman v3.9 dalam satu grid
      `position: fixed` (design/StudioShell.jsx), jadi setiap tekanan pada
      halaman ini akan lulus semakan itu dan pilihan formula tidak akan
      pernah dibatalkan. Yang membezakan lapisan terapung sebenar ialah
      z-index-nya: shell = 50, menu chip = 210, modal = 3000-9999. */
/* ⚠️ KEMBAR: salinan dalam GenerateV2Komposer.jsx (Workspace v4, 26 Okt). Ubah satu = ubah dua. */
const AMBANG_LAPISAN = 100;
function atasLapisanTerapung(el) {
    for (let n = el; n && n !== document.body; n = n.parentElement) {
        const g = window.getComputedStyle(n);
        if (g.position === 'fixed' && (parseInt(g.zIndex, 10) || 0) >= AMBANG_LAPISAN) return true;
    }
    return false;
}

/* ---------- Data contoh --------------------------------------------- */

/* ⭐ MEDIA DIAMBIL DARIPADA HERO SALESPAGE — `MODULE_SHOWCASE` dalam
   [SalesPageView.jsx]. Kandungan yang sama seperti yang menarik pelanggan
   masuk, jadi tiada aset baharu perlu dicari.

   ⚠️ TETAPI BUKAN FAIL YANG SAMA. Yang dirujuk di sini ialah salinan kecil
   dalam `public/salespage/thumbs/`, dijana oleh
   [scripts/build-formula-thumbs.cjs]. Fail salespage asal saiz penuh —
   ai8saat.jpg sahaja 7.1MB pada 3072px lebar — dan petak pratonton ini
   lebarnya cuma ~150px. Merujuk fail asal bermakna 17.5MB dimuat turun
   setiap kali tab Generate dibuka; salinan ini 556KB, 32× lebih kecil.

   ⛔ JANGAN tukar path ini kembali ke `/salespage/*.jpg`. Ia akan nampak
      sama pada skrin dan senyap mengembalikan bil data itu.

   Poster untuk kad bervideo diambil daripada BINGKAI PERTAMA video itu
   sendiri. Dahulu ia foto stok Unsplash yang langsung tiada kaitan dengan
   video yang dimainkan.

   Nak tambah video baharu: letak MP4 dalam `public/salespage/`, tambah
   namanya dalam senarai `VIDEO` skrip itu, jalankan skrip, kemudian isi
   `video` di bawah. */
const FORMULA = [
    {
        id: 'ai8saat', nama: 'AI8SAAT', kunciNota: 'workspace.composer.nota.ai8saat', durasi: '0:08',
        poster: '/salespage/thumbs/ai8saat.avif', video: '',
    },
    {
        id: 'ai16saat', nama: 'AI16SAAT', kunciNota: 'workspace.composer.nota.ai16saat', tag: 'BETA', durasi: '0:16',
        poster: '/salespage/thumbs/ai16saat.avif', video: '/salespage/thumbs/ai16saat-360.mp4',
    },
    {
        id: 'homeniche', nama: 'HOME NICHE', kunciNota: 'workspace.composer.nota.homeniche', tag: 'BETA', durasi: '0:16',
        poster: '/salespage/thumbs/homeniche.avif', video: '',
    },
    {
        id: 'goyang', nama: 'GOYANG', kunciNota: 'workspace.composer.nota.goyang', tag: 'BETA', durasi: '0:08',
        poster: '/salespage/thumbs/goyang.avif', video: '',
    },
    {
        id: 'smart', nama: 'SMART', kunciNota: 'workspace.composer.nota.smart', tag: 'NEW', durasi: '0:08',
        poster: '/salespage/thumbs/smartcustom.avif', video: '/salespage/thumbs/smartcustom-360.mp4',
    },
    {
        id: 'flownity', nama: 'FLOWNITY', kunciNota: 'workspace.composer.nota.flownity', tag: 'NEW', durasi: '0:40',
        poster: '/salespage/thumbs/flownity.avif', video: '',
    },
];

/* ---- Avatar ---------------------------------------------------------
   Bentuk data mengikut yang sebenar dalam App.jsx: avatar simpanan
   duduk di users/{uid}/avatars dengan {id, name, gender, age, imageUrl},
   ditambah dua pilihan terbina — Default (Male) dan Default (Female) —
   dan pilihan "tiada avatar". Had slot 5 (avatarMaxSlots).

   ⭐ BENTUKNYA SENARAI BARIS, bukan galeri petak (keputusan user
   2026-08-07, dipilih daripada empat cadangan). Sebabnya: avatar
   ditetapkan sekali, bukan ditukar setiap kali — ia tidak berbaloi
   mengambil ruang lebih daripada chip lain di sebelahnya. Baris juga
   muat nama penuh berserta gender dan umur, yang petak terpaksa
   buang. Ia guna `.s-pop__row` yang sama seperti menu chip lain,
   jadi tiada bentuk baharu diperkenalkan.

   ⛔ JANGAN kembalikan emoji atau bulatan warna-warni. Petak muka
      memaparkan GAMBAR avatar pada halaman sebenar; hiasan yang
      terlalu terang bertentangan dengan tema dan akan dibuang juga
      apabila gambar sebenar masuk. Ganti sementara = huruf pertama
      nama, monokrom.

   ⚠️ PROTOTAIP: `inisial` menggantikan `imageUrl`. */
const SLOT_AVATAR = 5;

/* `kunci` = kunci kamus; `nama` kekal sebagai sandaran. Mod SEBENAR
   menghantar senarai avatarnya sendiri (nama pelanggan, bukan ayat
   UI) dan senarai itu tiada `kunci` — jadi ia dipapar apa adanya. */
const AVATAR_TETAP = [
    { id: 'none', kunci: 'workspace.composer.avatarNone', nama: 'No avatar', kosong: true },
    { id: '__male__', kunci: 'workspace.composer.avatarDefaultMale', nama: 'Default L', inisial: 'L' },
    { id: '__female__', kunci: 'workspace.composer.avatarDefaultFemale', nama: 'Default P', inisial: 'P' },
];

const AVATAR_SIMPANAN = [
    { id: 'a1', nama: 'Aisyah', inisial: 'A', gender: 'Female', umur: 24 },
    { id: 'a2', nama: 'Faiz', inisial: 'F', gender: 'Male', umur: 31 },
];

const GAYA = [
    {
        id: 'auto',
        kunci: 'workspace.composer.style.auto', nama: 'Auto',
        kunciNota: 'workspace.composer.style.autoNote',
        nota: 'AI memilih hook dan style terbaik mengikut product anda.',
    },
    {
        id: 'pro',
        kunci: 'workspace.composer.style.pro', nama: 'Pro Mode',
        kunciNota: 'workspace.composer.style.proNote',
        nota: 'Anda menetapkan hook dan style sendiri untuk setiap set.',
    },
];

/* `STOK_PRODUK` DIBUANG — lampiran kini dibaca daripada `LIBRARY` di
   bawah, sumber yang SAMA dengan pemilih produk. Dua senarai berasingan
   bermakna pil composer dan pemilih boleh tidak bersetuju tentang apa
   yang sudah dilampirkan. */

/* Bilangan pil yang dipapar sebelum selebihnya dilipat. Enam kerana
   itu lebih kurang dua baris pada lebar composer — cukup untuk
   pengguna mengesahkan pilihannya tanpa lampiran menenggelamkan
   kotak input. */
const PIL_TERBUKA = 6;

/* ---- Library produk -------------------------------------------------
   Isi pemilih produk. Bentuk data mengikut modal "Product Library"
   sebenar: dua sumber (System / My Products), penanda READY vs PENDING
   (`hasIntelligence`), kumpulan, dan sebab kenapa sesuatu produk belum
   boleh diguna.

   ⛔ PENDING TIDAK BOLEH DIPILIH — peraturan sedia ada, bukan hiasan.
      Produk tanpa data tidak boleh dijana; membenarkannya ditanda cuma
      memindahkan kegagalan ke saat Generate ditekan, selepas pengguna
      sudah menunggu. Sebab itu ia dikunci di sini, dengan sebabnya
      dieja terus pada baris itu. */
const LIBRARY = [
    {
        id: 'L1', sumber: 'system', sku: '1729644732671100397', kumpulan: 'topsales',
        nama: 'BELLA PARK PERFUME PINK EDITION',
        url: 'shop.tiktok.com/view/product/1729644732671100397',
        sedia: false, sebabKunci: 'workspace.composer.picker.reasonNoData',
        thumb: 'linear-gradient(135deg,#f9a8d4,#be185d)',
    },
    {
        id: 'L2', sumber: 'system', sku: '1732855180384109694', kumpulan: 'topsales',
        nama: 'QUSTERE HYDROCOLLOID ROLL — 2 IN X 6.6 FT untuk pimple',
        url: 'shop.tiktok.com/my/pdp/pembalut-hidrokoloid-qustere',
        sedia: false, sebabKunci: 'workspace.composer.picker.reasonIncomplete',
        thumb: 'linear-gradient(135deg,#a7f3d0,#047857)',
    },
    {
        id: 'L3', sumber: 'system', sku: '1731429181059204820', kumpulan: 'winning',
        nama: 'PERFUME PARADISE MAJESTIC EDITION — PEN 10ML PARFUM',
        url: 'shop.tiktok.com/my/pdp/perfume-paradise-majestic-edition-10ml',
        sedia: true, thumb: 'linear-gradient(135deg,#fbcfe8,#9d174d)',
    },
    {
        id: 'L4', sumber: 'system', sku: '1729551811249342325', kumpulan: 'topsales',
        nama: 'HOTOP 360° ROTATABLE CAR PHONE HOLDER FOR DASHBOARD',
        url: 'shop.tiktok.com/my/pdp/pemegang-telefon-kereta-360',
        sedia: true, thumb: 'linear-gradient(135deg,#93c5fd,#1d4ed8)',
    },
    {
        id: 'L5', sumber: 'system', sku: '1730118845220123991', kumpulan: 'winning',
        nama: 'Set Tudung Bawal Premium Cotton Voal',
        url: 'shop.tiktok.com/my/pdp/set-tudung-bawal-premium',
        sedia: true, thumb: 'linear-gradient(135deg,#fcd34d,#b45309)',
    },
    {
        id: 'L6', sumber: 'own', sku: '1729480091223841902', kumpulan: 'winning',
        nama: 'Minyak Wangi Attar Musk Al-Haramain 12ml',
        url: 'shop.tiktok.com/my/pdp/attar-musk-al-haramain',
        sedia: true, thumb: 'linear-gradient(135deg,#a5b4fc,#4338ca)',
    },
    {
        id: 'L7', sumber: 'own', sku: '1729478550118822943', kumpulan: 'topsales',
        nama: 'Umpan Semut Ant Bait Roach Killer Gel 10g',
        url: 'shop.tiktok.com/my/pdp/umpan-semut-gel',
        sedia: false, sebabKunci: 'workspace.composer.picker.reasonNoData',
        thumb: 'linear-gradient(135deg,#86efac,#15803d)',
    },
    {
        id: 'L8', sumber: 'own', sku: '1729477221950113374', kumpulan: 'topsales',
        nama: 'DRY Pro Baju Hujan Motorcycle Rainsuit Coat',
        url: 'shop.tiktok.com/my/pdp/dry-pro-baju-hujan',
        sedia: true, thumb: 'linear-gradient(135deg,#fca5a5,#b91c1c)',
    },
    {
        id: 'L9', sumber: 'own', sku: '1729476003881224165', kumpulan: 'winning',
        nama: 'Official Glow Skin Toner Hydrating Brightening 100ml',
        url: 'shop.tiktok.com/my/pdp/official-glow-skin-toner',
        sedia: true, thumb: 'linear-gradient(135deg,#67e8f9,#0e7490)',
    },
    {
        id: 'L10', sumber: 'own', sku: '1730992114550120118', kumpulan: 'topsales',
        nama: 'Penapis Air Paip Dapur 8 Lapisan',
        url: 'shop.tiktok.com/my/pdp/penapis-air-paip-dapur',
        sedia: true, thumb: 'linear-gradient(135deg,#5eead4,#0f766e)',
    },
];

/* `value` ialah nilai LOGIK (ditapis dengan `p.sumber`) — ia tidak
   pernah diterjemah. Yang diterjemah cuma labelnya, dan label itu
   dibina pada masa render kerana ia membawa bilangan produk. */
const SUMBER_TAB = [
    {
        value: 'system', kunci: 'workspace.composer.picker.tabSystem',
        n: LIBRARY.filter((p) => p.sumber === 'system').length,
    },
    {
        value: 'own', kunci: 'workspace.composer.picker.tabMyProducts',
        n: LIBRARY.filter((p) => p.sumber === 'own').length,
    },
];

/* ⛔ "Winning Product" dan "Top Sales" ialah NAMA GROUP pelanggan —
   data, bukan ayat UI. Ia datang daripada Firestore pada halaman
   sebenar, jadi ia kekal apa adanya dalam kedua-dua bahasa. Hanya
   "Semua group" (pilihan penapis) yang diterjemah. */
const KUMPULAN = [
    { id: 'all', kunci: 'workspace.composer.picker.groupAll', label: 'All groups' },
    { id: 'winning', label: 'Winning Product' },
    { id: 'topsales', label: 'Top Sales' },
];

/* Nama group untuk dipapar pada kad. `all` dikecualikan — ia pilihan
   penapis, bukan group sebenar yang mana-mana produk boleh miliki. */
const namaKumpulan = (id) => (id && id !== 'all'
    ? KUMPULAN.find((k) => k.id === id)?.label || null
    : null);

/* ---------- Kotak semak ---------------------------------------------- */

function Kotak({ on, mati, onChange }) {
    return (
        <button
            type="button" role="checkbox" aria-checked={!!on} disabled={mati}
            onClick={(e) => { e.stopPropagation(); onChange?.(); }}
            style={{
                width: 16, height: 16, flex: 'none', padding: 0, borderRadius: 5,
                display: 'grid', placeItems: 'center',
                cursor: mati ? 'not-allowed' : 'pointer', opacity: mati ? .35 : 1,
                border: `1px solid ${on ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
                background: on ? 'var(--s-select)' : 'transparent',
            }}
        >
            {on && <Check size={11} strokeWidth={3} style={{ color: 'var(--s-select-ink)' }} />}
        </button>
    );
}

/* ---------- Chip ----------------------------------------------------- */

function Chip({ icon, children, caret, on, onClick, title, style, label }) {
    return (
        <button
            type="button"
            onClick={onClick}
            title={title}
            aria-label={label}
            className={cx('s-chip', !children && 's-chip--icon', on && 's-chip--on')}
            style={style}
        >
            {icon}
            {children && <span>{children}</span>}
            {caret && <ChevronDown size={13} strokeWidth={2} style={{ opacity: .6, flex: 'none' }} />}
        </button>
    );
}

/* Chip yang membuka senarai pilihan. Menu ditutup bila diklik di
   luar atau bila Esc ditekan — tanpa itu ia tersangkut terbuka dan
   menutup baris chip yang lain. */
/* `minWidth: 0` pada pembalut juga — tanpa ia, chip di dalamnya tidak
   dapat mengecut walaupun `.s-chip` membenarkannya. */
function ChipMenu({ icon, label, lebar = 260, padat, children }) {
    const [buka, setBuka] = useState(false);
    const rujuk = useRef(null);

    useEffect(() => {
        if (!buka) return undefined;
        const luar = (e) => { if (rujuk.current && !rujuk.current.contains(e.target)) setBuka(false); };
        const esc = (e) => { if (e.key === 'Escape') setBuka(false); };
        document.addEventListener('mousedown', luar);
        document.addEventListener('keydown', esc);
        return () => {
            document.removeEventListener('mousedown', luar);
            document.removeEventListener('keydown', esc);
        };
    }, [buka]);

    return (
        <div ref={rujuk} style={{ position: 'relative', minWidth: 0 }}>
            <Chip icon={icon} caret on={buka} onClick={() => setBuka((v) => !v)}>{label}</Chip>
            {buka && (
                <div
                    className="s-pop"
                    style={{ width: lebar, padding: padat ? 0 : undefined }}
                    onClick={() => setBuka(false)}
                >
                    {children}
                </div>
            )}
        </div>
    );
}

/* Petak muka. Saiz `s` untuk dalam chip, `m` untuk baris senarai.
   Pada halaman sebenar ia memaparkan `imageUrl`; di sini huruf. */
function Muka({ av, s, pilih }) {
    const sisi = s ? 18 : 26;
    return (
        <span
            className="s-avthumb"
            style={{
                width: sisi, height: sisi, borderRadius: s ? 5 : 7,
                fontSize: s ? 10 : 11,
                borderStyle: av.tambah ? 'dashed' : 'solid',
                borderColor: pilih ? 'var(--s-select)' : undefined,
            }}
        >
            {av.tambah ? <Plus size={s ? 11 : 13} strokeWidth={2} />
                : av.kosong ? <UserX size={s ? 11 : 14} strokeWidth={1.8} />
                    /* Mod sebenar menghantar `img` (avatar simpanan pelanggan).
                       Huruf kekal sebagai sandaran bila avatar tiada gambar —
                       dan itu jugalah yang dipakai sepanjang prototaip. */
                    : av.img ? <img src={av.img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 'inherit' }} />
                        : av.inisial}
        </span>
    );
}

/* Baris dalam menu chip. `hujung` untuk teks kecil di kanan (kaunter
   slot); `onSunting` memunculkan pensel bila baris dituding. */
function BarisPop({ tanda, nama, nota, dipilih, onClick, hujung, onSunting }) {
    const t = useT();
    return (
        <div className="s-pop__row" role="button" tabIndex={0} onClick={onClick}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick?.(); } }}>
            {tanda}
            <span style={{ flex: 1, minWidth: 0 }}>
                <span style={{ display: 'block', fontSize: 13, color: 'var(--s-ink)' }}>{nama}</span>
                {nota && <span style={{ display: 'block', fontSize: 11.5, color: 'var(--s-ink-3)', marginTop: 1 }}>{nota}</span>}
            </span>

            {/* Sunting hanya pada avatar simpanan — "Tiada" dan Default
                bukan milik pengguna. Ia muncul bila dituding sahaja;
                lima pensel yang kekal kelihatan menenggelamkan nama
                yang sepatutnya dibaca. Padam duduk di dalam skrin
                sunting, bukan di sini. */}
            {onSunting && (
                <span
                    role="button" tabIndex={0} aria-label={t('workspace.composer.editAvatar')} className="s-pop__edit"
                    onClick={(e) => { e.stopPropagation(); onSunting(); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.stopPropagation(); onSunting(); } }}
                >
                    <Pencil size={12} strokeWidth={2} />
                </span>
            )}
            {hujung && <span style={{ fontSize: 11.5, color: 'var(--s-ink-3)', flex: 'none' }}>{hujung}</span>}
            {dipilih && <Check size={14} strokeWidth={2.2} style={{ color: 'var(--s-ink)', flex: 'none' }} />}
        </div>
    );
}

/* ============================================================
   PEMILIH PRODUK  —  modal "Product Library"
   ============================================================
   Popup bertindih, susunan mengikut modal Product Library
   sebenar (arahan user 2026-08-13): kepala berikon, tab sumber,
   carian, susunan, jalur "guna baru-baru ini", penapis kumpulan,
   Pilih semua, notis PENDING, senarai berkotak-semak, dan jalur
   kaki Batal / Tambah.

   ⚠️ SEJARAH — ia pernah menjadi panel INLINE di bawah kad
      composer (keputusan user 2026-08-09). Sebabnya: pengguna
      nampak apa yang sudah dilampirkan SAMBIL memilih yang
      baharu, kerana modal menutup kad itu dan dia terpaksa
      mengingat apa yang sudah ada — puncanya produk yang sama
      ditambah dua kali.

      Modal ini membawa balik risiko itu, jadi ia dibayar semula
      DI DALAM modal, bukan diabaikan:
        · kiraan "N sudah dilampirkan" pada baris Pilih semua
        · lencana "Already added" pada baris berkenaan
        · baris itu DIKUNCI — ia tidak boleh ditanda dua kali
      Jangan buang ketiga-tiga ini; itu sahaja yang menahan
      masalah asal daripada kembali.

   ⛔ PENDING kekal tidak boleh dipilih. Lihat nota pada LIBRARY.
   ============================================================ */

/* Susunan senarai — bentuk yang SAMA seperti senarai produk
   /workspace-v3.9 (arahan user 2026-08-14): jenis disenaraikan sekali,
   arah ditukar dengan menekan jenis yang sama sekali lagi. Kawalannya
   sendiri dikongsi melalui [SusunMenuV39.jsx].

   Jenis "Module" workspace TIADA di sini — produk dalam library belum
   terikat pada mana-mana formula; ia hanya menjadi module selepas kad
   dijana. "Name" mengambil tempatnya. */
/* Kunci kamus sahaja — `SusunMenuV39` menerima senarai siap, jadi
   ayat dibina dalam komponen (lihat `susunanTeks` di bawah).

   Tip "1 → 10" dan "A → Z" TIADA kunci: ia simbol, bukan ayat, dan
   ia terbaca sama dalam kedua-dua bahasa. */
const SUSUNAN = [
    { id: 'nombor', kunci: 'workspace.composer.picker.sort.productId', lalai: 'asc', tip: { asc: '1 → 10', desc: '10 → 1' } },
    {
        id: 'masa', kunci: 'workspace.composer.picker.sort.time', lalai: 'desc',
        kunciTip: { asc: 'workspace.composer.picker.sort.timeAsc', desc: 'workspace.composer.picker.sort.timeDesc' },
    },
    { id: 'nama', kunci: 'workspace.composer.picker.sort.name', lalai: 'asc', tip: { asc: 'A → Z', desc: 'Z → A' } },
    /* Lalai READY dahulu — hanya produk READY yang boleh ditanda, jadi
       menyusun ikut status untuk melihat PENDING di atas hampir tidak
       pernah yang dicari. Arah terbalik kekal ada untuk pengguna yang
       memang mahu melihat mana yang perlu dilengkapkan. */
    {
        id: 'status', kunci: 'workspace.composer.picker.sort.status', lalai: 'desc',
        kunciTip: { asc: 'workspace.composer.picker.sort.statusAsc', desc: 'workspace.composer.picker.sort.statusDesc' },
    },
];

/* Paparan senarai — sama seperti halaman /products-v3.9: baris padat
   atau grid kad bergambar. Dua-duanya menunjukkan produk yang SAMA dan
   tunduk pada penapis yang sama; hanya bentuk barisnya berbeza. */
const KUNCI_PAPAR = 'sesaat_v39_pemilih_papar';

const bacaPapar = () => {
    try {
        const v = localStorage.getItem(KUNCI_PAPAR);
        return v === 'grid' || v === 'jadual' ? v : 'jadual';
    } catch { return 'jadual'; }
};

function PemilihProduk({ dilampirkan, onTambah, onTutup }) {
    const t = useT();
    const [sumber, setSumber] = useState('system');
    const [cari, setCari] = useState('');
    const [kumpulan, setKumpulan] = useState('all');
    /* Lalai "Time · newest first" — sama seperti senarai workspace. */
    const [susun, setSusun] = useState('masa');
    const [arah, setArah] = useState('desc');
    const [papar, setPapar] = useState(bacaPapar);
    const [tanda, setTanda] = useState(() => new Set());

    /* Paparan pilihan diingat per-browser — corak sama seperti halaman
       /products-v3.9. Modal ini dibuka berpuluh kali sehari; memaksa
       pengguna menekan semula suis setiap kali adalah kerja berulang
       yang tiada sebabnya. */
    useEffect(() => {
        try { localStorage.setItem(KUNCI_PAPAR, papar); } catch { /* mod privasi */ }
    }, [papar]);

    /* Esc menutup modal — jangkaan asas bagi apa-apa popup bertindih.
       Tanpa ia satu-satunya jalan keluar ialah membidik butang X. */
    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape') onTutup(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onTutup]);

    /* Senarai pilihan dibina di sini, bukan pada aras modul — pemalar
       tidak boleh memanggil hook terjemahan. Nilai LOGIK (`value`,
       `id`) datang terus daripada pemalar dan tidak disentuh. */
    const sumberTab = SUMBER_TAB.map((s) => ({ value: s.value, label: t(s.kunci, { n: s.n }) }));
    const kumpulanPilihan = KUMPULAN.map((k) => ({ id: k.id, label: k.kunci ? t(k.kunci) : k.label }));
    const susunanPilihan = SUSUNAN.map((s) => ({
        ...s,
        label: t(s.kunci),
        tip: s.kunciTip ? { asc: t(s.kunciTip.asc), desc: t(s.kunciTip.desc) } : s.tip,
    }));

    const senarai = LIBRARY
        .filter((p) => p.sumber === sumber)
        .filter((p) => kumpulan === 'all' || p.kumpulan === kumpulan)
        .filter((p) => {
            const q = cari.trim().toLowerCase();
            return !q || p.nama.toLowerCase().includes(q) || p.sku.includes(q);
        })
        /* `slice()` dahulu — `sort` menyusun array asal di tempatnya, dan
           tanpa salinan ia akan mengocok LIBRARY sendiri secara kekal. */
        .slice()
        .sort((a, b) => {
            /* ⚠️ PROTOTAIP: "Time" disusun mengikut kedudukan dalam LIBRARY,
               bukan cap masa — data contoh tiada tarikh. Peraturan sama
               seperti senarai workspace. Jangan susun ikut `id`: id produk
               sebenar bukan turutan masa. */
            const banding = {
                nombor: (x, y) => String(x.sku).localeCompare(String(y.sku), undefined, { numeric: true }),
                masa: (x, y) => LIBRARY.indexOf(x) - LIBRARY.indexOf(y),
                nama: (x, y) => x.nama.localeCompare(y.nama),
                /* Status sahaja yang dibandingkan; produk yang SAMA
                   statusnya kekal pada susunan asal (sort JS stabil), jadi
                   dalam kumpulan READY turutannya tetap ikut masa. */
                status: (x, y) => (x.sedia === y.sedia ? 0 : x.sedia ? 1 : -1),
            }[susun] || ((x, y) => LIBRARY.indexOf(x) - LIBRARY.indexOf(y));

            return banding(a, b) * (arah === 'asc' ? 1 : -1);
        });

    /* Boleh ditanda = ada data DAN belum dilampirkan. Dua sebab berbeza,
       tetapi kedua-duanya bermakna baris itu tidak boleh dipetik. */
    const bolehTanda = (p) => p.sedia && !dilampirkan.includes(p.id);
    const yangBoleh = senarai.filter(bolehTanda);
    const semuaDitanda = yangBoleh.length > 0 && yangBoleh.every((p) => tanda.has(p.id));
    const bilPending = senarai.filter((p) => !p.sedia).length;

    /* Kuantiti = berapa KAD yang dibuat daripada satu produk yang sama.
       Makna yang SAMA seperti modal Product Library sebenar (had 1–20 di
       sana; had yang sama dikekalkan di sini supaya angka tidak berlainan
       antara dua tempat).

       Ia mesti bergerak SERENTAK dengan `tanda`: produk yang dibuang
       pilihannya kehilangan kuantitinya sekali. Kalau tidak, nombor lama
       muncul semula bila produk itu dipilih semula — pengguna nampak "3"
       pada produk yang dia baru sahaja tanda buat pertama kali. */
    const [kuantiti, setKuantiti] = useState({});

    const petik = (id) => {
        const sudahAda = tanda.has(id);
        setTanda((prev) => {
            const next = new Set(prev);
            if (sudahAda) next.delete(id); else next.add(id);
            return next;
        });
        setKuantiti((prev) => {
            const next = { ...prev };
            if (sudahAda) delete next[id]; else next[id] = 1;
            return next;
        });
    };

    const tukarKuantiti = (id, ubah) => setKuantiti((prev) => ({
        ...prev,
        [id]: Math.max(1, Math.min(20, (prev[id] || 1) + ubah)),
    }));

    const kosongkan = () => { setTanda(new Set()); setKuantiti({}); };

    /* Senarai troli dibaca daripada LIBRARY PENUH, bukan `senarai` yang
       sudah ditapis — kalau tidak, produk yang dipilih dalam tab System
       hilang daripada troli sebaik pengguna bertukar ke My Products,
       walaupun ia masih akan ditambah. */
    const produkTerpilih = LIBRARY.filter((p) => tanda.has(p.id));
    const jumlahKad = produkTerpilih.reduce((n, p) => n + (kuantiti[p.id] || 1), 0);

    return (
        /* Klik pada lapisan gelap menutup modal; klik di dalam panel tidak
           (`stopPropagation`) — kalau tidak, setiap kali pengguna menanda
           kotak semak modal akan tertutup. */
        <div
            className="s-modal"
            role="dialog"
            aria-modal="true"
            aria-label={t('workspace.composer.picker.title')}
            onClick={onTutup}
        >
            {/* Modal + troli ialah DUA kad bersaudara, bukan kad dalam kad —
                susunan yang sama seperti modal Product Library sebenar. Lebar
                keseluruhan mengembang bila troli muncul supaya senarai produk
                TIDAK mengecut; kalau ia mengecut, setiap kali pengguna menanda
                produk pertama seluruh senarai akan tersentak ke kiri. */}
            <div
                style={{
                    display: 'flex', alignItems: 'stretch', gap: 10,
                    /* 900 kotak Products + 10 jurang + 300 troli = 1210.
                       Angka dipilih user 2026-08-14.

                       Lebar TETAP — troli sentiasa terbuka, jadi tiada
                       lompatan lebar pada saat produk pertama ditanda.
                       ⚠️ Tiga angka ini mesti bergerak bersama: tukar lebar
                       troli di bawah tanpa menukar jumlah di sini akan
                       menyempitkan kotak Products, bukan melebarkan modal. */
                    width: '100%', maxWidth: 1210,
                    maxHeight: '88vh', minHeight: 0, flexWrap: 'wrap',
                    justifyContent: 'center',
                }}
            >
            <div
                className="s-modal__panel"
                /* Tinggi TETAP (pilihan user 2026-08-13): senarai kekal
                   setinggi ~11 baris walaupun hanya 5 produk sepadan. Tanpa
                   ia, kotak mengecut dan mengembang setiap kali pengguna
                   menukar tab atau menaip dalam carian — dan butang Add
                   melompat naik-turun di bawah tetikusnya.

                   `maxHeight` daripada `.s-modal__panel` (88vh) masih
                   berkuat kuasa: pada skrin rendah, 600px itu dipotong
                   supaya kepala dan jalur kaki tidak terkeluar skrin. */
                style={{ maxWidth: 'none', flex: '1 1 480px', minWidth: 0, height: 600 }}
                onClick={(e) => e.stopPropagation()}
            >

                {/* Kepala PADAT — satu baris (arahan user 2026-08-13, arah B).
                    Petak ikon dan ayat perihal dibuang: modal ini dibuka
                    daripada butang yang sudah bernama "Add Product", jadi ayat
                    yang menerangkan semula apa itu library hanya mengambil
                    ruang yang senarai perlukan. */}
                <header className="s-modal__head" style={{ padding: '11px 14px' }}>
                    <span style={{ fontSize: 14, fontWeight: 600, color: 'var(--s-ink)', flex: 'none' }}>
                        {t('workspace.composer.picker.title')}
                    </span>
                    <span className="s-dim" style={{ flex: 1, minWidth: 0 }}>{senarai.length}</span>
                    <button
                        type="button" onClick={onTutup}
                        aria-label={t('workspace.composer.picker.close')}
                        title={t('workspace.composer.picker.closeTip')}
                        className="s-modal__x"
                    >
                        <X size={14} strokeWidth={2} />
                    </button>
                </header>

                {/* SATU baris alat: tab · carian · group · susunan.
                    Group bertukar daripada baris pil kepada pemilih. Pada data
                    sebenar ada SEBELAS group dan lapan daripadanya berangka 0 —
                    sebagai pil ia memakan hampir seperempat tinggi modal untuk
                    penapis yang kebanyakannya tidak boleh ditekan. */}
                <div style={{
                    flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
                    padding: '9px 14px', borderBottom: '1px solid var(--s-line)', flexWrap: 'wrap',
                }}>
                    <Segmented value={sumber} onChange={setSumber} options={sumberTab} />

                    {/* Ruang lapang memisahkan DUA jenis kawalan yang kerjanya
                        berlainan: tab di kiri menukar SUMBER produk, manakala
                        carian/group/susunan di kanan menapis apa yang sudah
                        ada. Bila carian melekat pada tab, keempat-empatnya
                        membaca sebagai satu deretan butang tanpa memberitahu
                        mana satu buat apa. */}
                    <span style={{ flex: 1, minWidth: 12 }} />

                    <Field
                        icon={<Search size={14} strokeWidth={1.9} />}
                        placeholder={t('workspace.composer.picker.searchPlaceholder')}
                        value={cari}
                        onChange={(e) => setCari(e.target.value)}
                        /* Lebar TETAP, tidak meregang mengikut baki ruang —
                           medan carian yang membesar sampai 400px membaca
                           sebagai kawasan menaip utama halaman, sedangkan ia
                           salah satu daripada tiga penapis setaraf. */
                        style={{ flex: 'none', width: 190, minWidth: 0, height: 32, fontSize: 13 }}
                    />
                    <PilihV39
                        ariaLabel={t('workspace.composer.picker.groupAria')}
                        lebar="auto"
                        tinggi={32}
                        saizTeks={12.5}
                        lebarMenu={180}
                        nilai={kumpulan}
                        onPilih={setKumpulan}
                        pilihan={kumpulanPilihan}
                    />
                    <SusunMenuV39
                        pilihan={susunanPilihan}
                        jenis={susun}
                        arah={arah}
                        onTukar={(j, a) => { setSusun(j); setArah(a); }}
                    />

                    {/* Suis paparan — bentuk dan kelas yang SAMA seperti
                        halaman /products-v3.9. Ia tetapan pandangan, bukan
                        penapis, jadi ia duduk di HUJUNG bar selepas segala
                        yang menapis. */}
                    <div className="s-viewseg">
                        <button
                            type="button"
                            aria-label={t('workspace.composer.picker.gridView')}
                            title={t('workspace.composer.picker.gridView')}
                            aria-pressed={papar === 'grid'}
                            className={`s-viewseg__b${papar === 'grid' ? ' s-viewseg__b--on' : ''}`}
                            onClick={() => setPapar('grid')}
                        >
                            <LayoutGrid size={14} strokeWidth={1.9} />
                        </button>
                        <button
                            type="button"
                            aria-label={t('workspace.composer.picker.listView')}
                            title={t('workspace.composer.picker.listView')}
                            aria-pressed={papar === 'jadual'}
                            className={`s-viewseg__b${papar === 'jadual' ? ' s-viewseg__b--on' : ''}`}
                            onClick={() => setPapar('jadual')}
                        >
                            <Rows3 size={14} strokeWidth={1.9} />
                        </button>
                    </div>
                </div>

                {/* Jalur nipis: pilih semua · kunci PENDING · kiraan lampiran.
                    Notis PENDING dahulunya kotak amber tiga baris; dalam susunan
                    padat ia jadi satu baris dan ayat penuhnya berpindah ke
                    tooltip. ⚠️ Skrin sentuh tiada tooltip — kalau modal ini
                    dibawa ke telefon, ayat itu perlu tempat yang kelihatan. */}
                <div style={{
                    flex: 'none', display: 'flex', alignItems: 'center', gap: 9,
                    padding: '7px 14px', borderBottom: '1px solid var(--s-line)', flexWrap: 'wrap',
                }}>
                    <Kotak
                        on={semuaDitanda}
                        mati={yangBoleh.length === 0}
                        onChange={() => {
                            setTanda((prev) => {
                                const next = new Set(prev);
                                yangBoleh.forEach((p) => (semuaDitanda ? next.delete(p.id) : next.add(p.id)));
                                return next;
                            });
                            setKuantiti((prev) => {
                                const next = { ...prev };
                                yangBoleh.forEach((p) => {
                                    if (semuaDitanda) delete next[p.id];
                                    else if (!next[p.id]) next[p.id] = 1;
                                });
                                return next;
                            });
                        }}
                    />
                    <span style={{ fontSize: 12, color: 'var(--s-ink-2)' }}>
                        {t('workspace.composer.picker.selectAll', { n: yangBoleh.length })}
                    </span>

                    {bilPending > 0 && (
                        <span
                            title={t('workspace.composer.picker.pendingLockedTip', { n: bilPending })}
                            style={{
                                display: 'inline-flex', alignItems: 'center', gap: 5,
                                fontSize: 11.5, color: 'var(--s-warn)',
                            }}
                        >
                            <Info size={12} strokeWidth={2} />
                            {t('workspace.composer.picker.pendingLocked', { n: bilPending })}
                        </span>
                    )}

                    {/* Kiraan lampiran semasa — inilah yang menggantikan
                        keupayaan melihat pil composer semasa memilih. Lihat
                        nota di atas fungsi ini sebelum membuangnya. */}
                    {dilampirkan.length > 0 && (
                        <span className="s-hint" style={{ marginTop: 0, marginLeft: 'auto' }}>
                            {t('workspace.composer.picker.alreadyAttached', { n: dilampirkan.length })}
                        </span>
                    )}
                </div>

                {/* Senarai — SATU-SATUNYA kawasan yang menatal dalam modal ini.
                    Library sebenar boleh mencecah 90 produk; tanpa `minHeight:0`
                    bekas flex enggan mengecut dan senarai menolak jalur kaki
                    keluar dari pandangan.

                    Baris PADAT satu baris (arah B): baris URL penuh dibuang —
                    ia berulang hampir sama pada setiap produk dan hanya
                    menggandakan tinggi baris. Sebab PENDING pula berpindah ke
                    tooltip baris. */}
                <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
                {senarai.length === 0 ? (
                    <p className="s-hint" style={{ padding: '18px 14px 22px' }}>
                        {t('workspace.composer.picker.empty')}
                    </p>
                ) : papar === 'grid' ? (
                    /* Grid mengukur dirinya pada lebar MODAL, bukan lebar
                       tetingkap. `.s-pgrid` halaman Products memakai media
                       query viewport — di dalam modal berlebar tetap, ia
                       akan memberi bilangan lajur yang tiada kaitan dengan
                       ruang yang benar-benar ada. */
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(186px, 1fr))',
                        gap: 10, padding: 14,
                    }}>
                        {senarai.map((p) => {
                            const sudah = dilampirkan.includes(p.id);
                            return (
                                <KadPemilih
                                    key={p.id}
                                    p={p}
                                    on={tanda.has(p.id)}
                                    sudah={sudah}
                                    kunci={!p.sedia || sudah}
                                    onPetik={() => petik(p.id)}
                                />
                            );
                        })}
                    </div>
                ) : senarai.map((p) => {
                    const sudah = dilampirkan.includes(p.id);
                    const kunci = !p.sedia || sudah;
                    return (
                        <div
                            key={p.id}
                            onClick={() => !kunci && petik(p.id)}
                            title={p.sedia ? `${p.nama}\n${p.url}` : `${p.nama}\n${t(p.sebabKunci)}`}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 9,
                                padding: '6px 14px', borderBottom: '1px solid var(--s-line)',
                                cursor: kunci ? 'default' : 'pointer',
                                background: tanda.has(p.id) ? 'var(--s-fill)' : 'transparent',
                            }}
                        >
                            <Kotak on={tanda.has(p.id) || sudah} mati={kunci} onChange={() => petik(p.id)} />

                            {/* Petak gambar monokrom. Gambar produk sebenar
                                belum ada di sini, dan isian gradien berwarna
                                sebagai ganti akan dibuang semula sebaik gambar
                                sebenar masuk — jadi ia menilai reka bentuk atas
                                hiasan sementara. */}
                            <span style={{
                                width: 22, height: 22, flex: 'none', borderRadius: 6,
                                display: 'grid', placeItems: 'center',
                                background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                                color: 'var(--s-ink-3)', opacity: kunci ? .45 : 1,
                            }}>
                                <Package size={11} strokeWidth={1.6} />
                            </span>

                            {/* ID ialah LAJUR DEPAN, lebarnya tetap (pilihan
                                user 2026-08-14). Lebar tetap itulah maksudnya:
                                ID setiap baris bermula dan berakhir pada titik
                                yang SAMA, jadi mata boleh mengimbas lajur itu
                                ke bawah. Kalau ia dibiar mengecut ikut baki
                                ruang, ia berpindah tempat setiap baris dan ID
                                penuh hilang gunanya. */}
                            {/* ⚠️ 152px, bukan kurang. ID TikTok ialah 19 digit
                                dan user mahu ia PENUH — pada 10.5px Cal Sans
                                tabular ia memakan ~145px. Lebar 128px yang
                                dicuba dahulu memotongnya jadi "…123", iaitu
                                keadaan yang lajur ini sepatutnya elakkan. */}
                            <span className="s-num" style={{
                                flex: 'none', width: 152, fontSize: 10.5, opacity: kunci ? .6 : 1,
                                whiteSpace: 'nowrap',
                            }}>
                                #{p.sku}
                            </span>

                            {/* Nama DAN lencana "Added" berkongsi satu sel yang
                                meregang. Lencana itu mesti duduk DI DALAM sel
                                ini, bukan di antara sel — kalau ia sel sendiri,
                                baris yang sudah dilampirkan akan menolak lajur
                                group ke kiri dan jajaran lajur itu pecah pada
                                baris-baris tersebut sahaja. */}
                            <span style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 7 }}>
                                <span style={{
                                    minWidth: 0, fontSize: 12.5,
                                    color: kunci ? 'var(--s-ink-3)' : 'var(--s-ink)',
                                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                }}>
                                    {p.nama}
                                </span>
                                {sudah && (
                                    <Badge tone="brand" style={{ fontSize: 10, padding: '0 6px', flex: 'none' }}>
                                        {t('workspace.composer.picker.added')}
                                    </Badge>
                                )}
                            </span>

                            {/* Lajur group berlebar TETAP dan rata KIRI (arahan
                                user 2026-08-14). Nama group panjangnya berbeza
                                ("Winning Product" lawan "Top Sales"); tanpa
                                lebar tetap, setiap chip bermula pada titik yang
                                berlainan dan lajur ini nampak bengkok.

                                Baris tanpa group mengekalkan sel kosong —
                                itulah yang menahan lajur status di kanannya
                                daripada berganjak. */}
                            <span style={{ flex: 'none', width: 120, display: 'flex', minWidth: 0 }}>
                                {namaKumpulan(p.kumpulan) && (
                                    <span className="s-rowchip" style={{ fontSize: 10.5, minWidth: 0, maxWidth: '100%' }}>
                                        <Tag size={10} strokeWidth={1.9} style={{ flex: 'none' }} />
                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                            {namaKumpulan(p.kumpulan)}
                                        </span>
                                    </span>
                                )}
                            </span>

                            <span style={{
                                flex: 'none', width: 52, textAlign: 'right', fontSize: 11,
                                color: p.sedia ? 'var(--s-ok)' : 'var(--s-warn)',
                            }}>
                                {p.sedia
                                    ? t('workspace.composer.picker.ready')
                                    : t('workspace.composer.picker.pending')}
                            </span>

                            {/* Petak mangga SENTIASA ada, walaupun kosong.
                                Dahulunya ikon ini hanya dirender pada baris
                                berkunci — dan kerana sel nama yang meregang
                                menyerap perbezaan itu, SETIAP lajur sebelumnya
                                berganjak ~21px ke kiri pada baris berkunci
                                sahaja. Hasilnya lajur group nampak bengkok
                                tepat pada baris PENDING. */}
                            <span style={{ flex: 'none', width: 13, display: 'grid', placeItems: 'center' }}>
                                {kunci && (
                                    <Lock size={12} strokeWidth={1.9} style={{ color: 'var(--s-ink-3)' }} />
                                )}
                            </span>

                            {/* ⛔ TIADA pelaras kuantiti di sini (arahan user
                                2026-08-13). Kuantiti diselaras di panel kanan
                                SAHAJA — satu tempat, bukan dua. Dua pelaras
                                untuk nombor yang sama bermakna baris senarai
                                berganjak lebar setiap kali produk ditanda, dan
                                nama produk terpotong lebih awal semata-mata
                                untuk memberi ruang kepada kawalan yang sudah
                                ada di sebelah. */}
                        </div>
                    );
                })}
            </div>

                <footer className="s-modal__foot">
                    <span className="s-dim" style={{ flex: 1, minWidth: 0 }}>
                        {tanda.size === 0
                            ? t('workspace.composer.picker.noneSelected')
                            : t('workspace.composer.picker.nSelected', { n: tanda.size })}
                    </span>
                    <span style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 'none' }}>
                        <Btn variant="ghost" size="sm" onClick={onTutup}>
                            {t('workspace.composer.picker.cancel')}
                        </Btn>
                        <Btn
                            size="sm"
                            disabled={tanda.size === 0}
                            icon={<ShoppingCart size={13} strokeWidth={2} />}
                            onClick={() => onTambah(produkTerpilih.map((p) => p.id))}
                        >
                            {t('workspace.composer.picker.add', { n: tanda.size })}
                        </Btn>
                    </span>
                </footer>
            </div>

            {/* ---------- Troli · SENTIASA terbuka -----------------------
                Susunan mengikut modal Product Library sebenar: kepala
                dengan kiraan + Kosongkan, senarai pilihan dengan pelaras
                kuantiti, dan jumlah kad di kaki.

                Ia dibuka serentak dengan panel utama walaupun belum ada
                pilihan (arahan user 2026-08-13). Panel yang menjelma pada
                klik pertama menyentak susun atur dan menyorok tempat
                kuantiti diselaras sehingga pengguna terlanggarnya sendiri.

                ⚠️ PROTOTAIP — pelaras kuantiti ialah PAPARAN. Dalam mod
                   sebenar kuantiti N memecah satu produk kepada N kad;
                   di sini composer melampirkan satu pil per produk kerana
                   pil dikunci pada id produk. Sebab itu butang "Add"
                   membilang PRODUK, bukan jumlah kad — supaya angka pada
                   butang sentiasa sama dengan bilangan pil yang muncul. */}
                <aside
                    onClick={(e) => e.stopPropagation()}
                    style={{
                        flex: '0 0 300px', maxWidth: 300, minHeight: 0, maxHeight: '88vh',
                        display: 'flex', flexDirection: 'column', overflow: 'hidden',
                        background: 'var(--s-panel)', border: '1px solid var(--s-line)',
                        borderRadius: 'var(--s-r-lg)',
                        boxShadow: '0 24px 64px -20px rgba(0, 0, 0, .7)',
                    }}
                >
                    <div style={{
                        flex: 'none', display: 'flex', alignItems: 'center', gap: 8,
                        padding: '13px 14px', borderBottom: '1px solid var(--s-line)',
                    }}>
                        <ShoppingCart size={14} strokeWidth={2} style={{ color: 'var(--s-ink-2)', flex: 'none' }} />
                        <span style={{ flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500, color: 'var(--s-ink)' }}>
                            {t('workspace.composer.picker.cartTitle', { n: tanda.size })}
                        </span>
                        {/* "Clear" muncul hanya bila ada sesuatu untuk
                            dikosongkan — butang mati yang kekal di situ pada
                            troli kosong hanya mengajar pengguna mengabaikannya. */}
                        {tanda.size > 0 && (
                            <button
                                type="button"
                                onClick={kosongkan}
                                title={t('workspace.composer.picker.clearTip')}
                                style={{
                                    flex: 'none', padding: 0, border: 0, background: 'none', cursor: 'pointer',
                                    fontFamily: 'inherit', fontSize: 11.5, color: 'var(--s-ink-3)',
                                }}
                            >
                                {t('workspace.composer.picker.clear')}
                            </button>
                        )}
                    </div>

                    <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: 8, display: 'flex', flexDirection: 'column', gap: 6 }}>
                        {/* Troli kosong menerangkan dirinya. Panel yang sentiasa
                            terbuka tetapi kosong tanpa sebarang ayat membaca
                            sebagai ruang yang gagal dimuatkan. */}
                        {produkTerpilih.length === 0 && (
                            <p className="s-hint" style={{ margin: '6px 4px 0', textAlign: 'center', lineHeight: 1.5 }}>
                                {t('workspace.composer.picker.cartEmptyLine1')}
                                <br />
                                {t('workspace.composer.picker.cartEmptyLine2')}
                            </p>
                        )}
                        {produkTerpilih.map((p) => (
                            <div
                                key={p.id}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: 8, padding: 7,
                                    background: 'var(--s-card)', border: '1px solid var(--s-line)',
                                    borderRadius: 10,
                                }}
                            >
                                <span style={{
                                    width: 30, height: 30, flex: 'none', borderRadius: 7,
                                    display: 'grid', placeItems: 'center',
                                    background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                                    color: 'var(--s-ink-3)',
                                }}>
                                    <Package size={13} strokeWidth={1.5} />
                                </span>

                                <span style={{ flex: 1, minWidth: 0 }}>
                                    <span className="s-num" style={{ display: 'block', fontSize: 11, lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                        #{p.sku}
                                    </span>
                                    <span style={{
                                        display: 'block', fontSize: 12, color: 'var(--s-ink)',
                                        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                                    }} title={p.nama}>{p.nama}</span>
                                </span>

                                <Pelaras nilai={kuantiti[p.id] || 1} onUbah={(d) => tukarKuantiti(p.id, d)} />

                                <button
                                    type="button"
                                    onClick={() => petik(p.id)}
                                    title={t('workspace.composer.picker.remove')}
                                    aria-label={t('workspace.composer.picker.removeAria')}
                                    style={{
                                        width: 20, height: 20, flex: 'none', padding: 0, borderRadius: 999,
                                        display: 'grid', placeItems: 'center', cursor: 'pointer',
                                        background: 'none', border: 0, color: 'var(--s-ink-3)',
                                    }}
                                >
                                    <X size={12} strokeWidth={2.2} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div style={{
                        flex: 'none', padding: '11px 14px', borderTop: '1px solid var(--s-line)',
                        background: 'var(--s-card-foot)',
                    }}>
                        <div style={{ fontSize: 10.5, fontWeight: 700, letterSpacing: '.08em', color: 'var(--s-ink-3)' }}>
                            {t('workspace.composer.picker.totalCard')}
                        </div>
                        <div style={{
                            fontSize: 20, fontWeight: 600, fontVariantNumeric: 'tabular-nums',
                            color: jumlahKad > 0 ? 'var(--s-ink)' : 'var(--s-ink-3)',
                        }}>
                            {jumlahKad}
                        </div>
                    </div>
                </aside>
            </div>
        </div>
    );
}

/* ---------- Kad produk (paparan Grid) ---------------------------------
   Bentuk sama seperti kad halaman /products-v3.9 — gambar di atas, tanda
   dan status bertindih pada gambar, nama dua baris di bawah.

   Baris ID kekal walaupun kad sempit: nama produk TikTok banyak yang
   hampir serupa, dan nama yang dipotong pada baris kedua tidak lagi
   membezakan keduanya. */
function KadPemilih({ p, on, sudah, kunci, onPetik }) {
    const t = useT();
    return (
        <div
            role="button"
            tabIndex={kunci ? -1 : 0}
            aria-pressed={on}
            onClick={() => !kunci && onPetik()}
            onKeyDown={(e) => {
                if (!kunci && (e.key === 'Enter' || e.key === ' ')) { e.preventDefault(); onPetik(); }
            }}
            title={p.sedia ? `${p.nama}\n${p.url}` : `${p.nama}\n${t(p.sebabKunci)}`}
            className={`s-card s-pcard${on ? ' s-pcard--on' : ''}`}
            style={{ cursor: kunci ? 'default' : 'pointer', opacity: kunci ? .5 : 1 }}
        >
            <div className="s-thumb s-thumb--besar">
                <Package size={20} strokeWidth={1.4} />

                <span style={{ position: 'absolute', top: 7, left: 7 }}>
                    <Kotak on={on || sudah} mati={kunci} onChange={onPetik} />
                </span>

                <span style={{
                    position: 'absolute', top: 7, right: 7,
                    fontSize: 10, padding: '1px 6px', borderRadius: 5,
                    background: 'var(--s-ground)', border: '1px solid var(--s-line)',
                    color: p.sedia ? 'var(--s-ok)' : 'var(--s-warn)',
                }}>
                    {p.sedia
                        ? t('workspace.composer.picker.ready')
                        : t('workspace.composer.picker.pending')}
                </span>
            </div>

            <div style={{ padding: '8px 9px 10px' }}>
                {/* ID PENUH, di ATAS nama (arahan user 2026-08-14). Ia tidak
                    dipendekkan: separuh ID tidak membezakan dua produk yang
                    ID-nya berkongsi awalan yang sama. Pada 10px, 19 digit
                    muat dalam kad 186px dengan ruang berbaki. */}
                <div className="s-num" style={{
                    fontSize: 10, lineHeight: 1.4,
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>
                    #{p.sku}
                </div>

                {/* Dua baris, kemudian dipotong dengan "…" — had itu yang
                    menjadikan tinggi setiap kad dalam grid sama rata. */}
                <div className="s-clamp2" style={{
                    fontSize: 12, lineHeight: 1.35, minHeight: 32, marginTop: 2, color: 'var(--s-ink)',
                }}>
                    {p.nama}
                </div>

                <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 7, minWidth: 0 }}>
                    {namaKumpulan(p.kumpulan) && (
                        <span className="s-rowchip" style={{ minWidth: 0, maxWidth: '100%' }}>
                            <Tag size={10} strokeWidth={1.9} style={{ flex: 'none' }} />
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {namaKumpulan(p.kumpulan)}
                            </span>
                        </span>
                    )}
                    {sudah && (
                        <Badge tone="brand" style={{ fontSize: 9.5, padding: '0 5px', flex: 'none' }}>
                            {t('workspace.composer.picker.added')}
                        </Badge>
                    )}
                </div>
            </div>
        </div>
    );
}

/* ---------- Pelaras kuantiti ------------------------------------------
   Dua butang bulat + angka. Monokrom: butang tambah TIDAK diberi warna
   jenama seperti modal lama — dalam senarai yang setiap barisnya ada satu,
   warna itu berulang sepuluh kali dan berhenti bermakna apa-apa. */
function Pelaras({ nilai, onUbah }) {
    const t = useT();
    const btn = (mati) => ({
        width: 20, height: 20, flex: 'none', padding: 0, borderRadius: 999,
        display: 'grid', placeItems: 'center',
        cursor: mati ? 'not-allowed' : 'pointer', opacity: mati ? .3 : 1,
        background: 'var(--s-fill)', border: '1px solid var(--s-line)', color: 'var(--s-ink-2)',
    });

    return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, flex: 'none' }}>
            <button
                type="button" aria-label={t('workspace.composer.picker.decrease')} disabled={nilai <= 1}
                onClick={() => onUbah(-1)} style={btn(nilai <= 1)}
            >
                <Minus size={11} strokeWidth={2.4} />
            </button>
            <span style={{
                minWidth: 14, textAlign: 'center', fontSize: 12, color: 'var(--s-ink)',
                fontVariantNumeric: 'tabular-nums',
            }}>{nilai}</span>
            <button
                type="button" aria-label={t('workspace.composer.picker.increase')} disabled={nilai >= 20}
                onClick={() => onUbah(1)} style={btn(nilai >= 20)}
            >
                <Plus size={11} strokeWidth={2.4} />
            </button>
        </span>
    );
}

/* ============================================================
   GALERI FORMULA
   ============================================================
   Semua formula TERPAMPANG serentak dengan pratonton 9:16 —
   bukan tersorok dalam dropdown teks (arahan user 2026-08-09).

   Sebabnya: nama seperti "AI16SAAT" atau "GOYANG" tidak
   memberitahu pelanggan apa-apa tentang rupa video yang bakal
   keluar. Satu bingkai video menjawabnya sebelum dia menekan
   apa-apa — dan itulah satu-satunya cara dia boleh memilih
   formula dengan yakin pada kali pertama.

   Bentuk 9:16 disengajakan: ia bentuk video TikTok yang
   dihasilkan, jadi pratonton ini juga mengajar apa yang dia
   akan dapat.

   ⚠️ PRATONTON = MOCKUP (gradien). Ganti `pratonton` dengan
      poster/video sebenar nanti; susun atur tidak perlu diubah.
   ============================================================ */

/* `senarai` + `contoh` + `onCipta` ditambah untuk mod sebenar:
     senarai — formula sebenar pelanggan (module yang dia ada), termasuk
               yang TERKUNCI. Lalai kekal data contoh, jadi prototaip
               tidak berubah walau satu piksel.
     contoh  — lencana "Sample preview". Ia jujur dalam prototaip; pada
               halaman sebenar video itu memang video formula sebenar,
               jadi lencana itu akan menipu.
     onCipta — petak "Create your own". Tanpa pengendali ia kekal mati,
               sama seperti dalam prototaip. */
function GaleriFormula({ pilihan, onPilih, onBatal, senarai = FORMULA, contoh = true, onCipta, onTetapan, onCiptaModule }) {
    const { lang, t } = useLang();

    /* Ayat kecil bawah nama formula. Tiga sumber, mengikut keutamaan:

         1. `notaMs`/`notaEn` — ayat yang ADMIN taip sendiri dalam modal
            tetapan module (arahan user 2026-08-18). Satu medan setiap
            bahasa, jadi kad kekal bertukar bahasa seperti bahagian lain.
         2. `kunciNota` — ayat asal daripada kamus.
         3. `nota` — nilai mentah, untuk formula ciptaan admin yang tiada
            dalam kamus.

       ⭐ `undefined` dan `''` BUKAN benda yang sama di sini, dan bezanya
          disengajakan (keputusan user 2026-08-18):
            undefined = admin belum pernah menyimpan module ini → guna ayat
                        asal, supaya kad tidak kosong pada hari ciri ini naik.
            ''        = admin membuka modal dan MENGOSONGKAN kotak itu →
                        kad memang tidak berayat.
          Sebab itu semakannya `!= null`, bukan sekadar `if (nilai)`. */
    const notaFormula = (f) => {
        const ditulisAdmin = lang === 'ms' ? f.notaMs : f.notaEn;
        if (ditulisAdmin != null) return ditulisAdmin;
        return f.kunciNota ? t(f.kunciNota) : f.nota;
    };
    const tipKunci = (f) => (f.kunciTip ? t(f.kunciTip) : f.tipKunci);

    /* ── BATAL PILIHAN BILA MENEKAN DI LUAR BARIS FORMULA ──────────────
       (arahan user 2026-08-18) Sebelum ini pilihan formula tidak boleh
       ditarik balik langsung — sekali tertekan, ia kekal bertanda.

       Kawasan yang DIKECUALIKAN (tekanan di situ tidak membatalkan):
         • baris formula itu sendiri
         • apa-apa yang bergantung pada pilihan itu — jalur tetapan
           (template GOYANG · sub-module · Suara Watak), jalur kaki
           (avatar · Generate), dan baris lampiran produk. Ditandakan
           dengan `data-kekal-formula` di dalam composer.
         • modal dan menu terapung

       ⛔ Tanpa pengecualian kedua itu, menekan Generate akan MEMBATALKAN
          formula sebelum tekanan itu sempat dikendalikan — dan Generate
          kemudiannya menolak dengan "pilih formula dahulu". */
    const zonRef = useRef(null);
    /* Pengendali batal disimpan dalam ref supaya pendengar tekanan tidak
       perlu dipasang semula setiap kali komponen dirender — App.jsx
       menciptanya semula sebagai fungsi baharu setiap render. */
    const batalRef = useRef(onBatal);
    useEffect(() => { batalRef.current = onBatal; });
    useEffect(() => {
        if (!pilihan || !batalRef.current) return undefined;
        const kena = (e) => {
            const el = e.target;
            if (!(el instanceof Element)) return;
            if (zonRef.current && zonRef.current.contains(el)) return;
            if (el.closest('[data-kekal-formula]')) return;
            if (atasLapisanTerapung(el)) return;
            batalRef.current?.();
        };
        /* Fasa TANGKAP: pengendali lain (cth kad produk) memanggil
           stopPropagation, jadi fasa gelembung tidak menerima tekanan itu. */
        document.addEventListener('mousedown', kena, true);
        return () => document.removeEventListener('mousedown', kena, true);
    }, [pilihan]);

    return (
        <div ref={zonRef} style={{ marginTop: 16 }}>
            <div style={{
                display: 'flex', alignItems: 'baseline', gap: 10,
                flexWrap: 'wrap', marginBottom: 10,
            }}>
                <span style={{ fontSize: 12, color: 'var(--s-ink-3)' }}>
                    {t('workspace.composer.formulaLabel')}
                </span>
                <span style={{ fontSize: 12, color: 'var(--s-ink-3)', flex: 1, minWidth: 0 }}>
                    {t('workspace.composer.formulaNote')}
                </span>
                {contoh && (
                    <Badge tone="warn" style={{ flex: 'none', fontSize: 11, padding: '0 6px' }}>
                        {t('workspace.composer.samplePreview')}
                    </Badge>
                )}
                {/* ★ [2026-10-17] ADMIN sahaja — cipta module baharu (modal yang sama dengan butang "+"
                    rupa lama). `onCiptaModule` undefined untuk bukan admin → tiada butang. */}
                {onCiptaModule && (
                    <button
                        type="button"
                        onClick={onCiptaModule}
                        style={{
                            flex: 'none', display: 'inline-flex', alignItems: 'center', gap: 4,
                            font: 'inherit', fontSize: 12, fontWeight: 500, lineHeight: 1.6,
                            padding: '1px 9px', borderRadius: 8, cursor: 'pointer',
                            background: 'transparent', color: 'var(--s-ink-2)',
                            border: '1px solid var(--s-line-strong)',
                        }}
                    >
                        <Plus size={13} strokeWidth={2} />
                        {t('workspace.composer.newModule')}
                    </button>
                )}
            </div>

            {/* Sebaik satu formula dipilih, yang lain PUDAR supaya mata terus
                jatuh pada pilihan itu (arahan user 2026-08-16). Selagi tiada
                pilihan, kelas ini tidak dipasang dan kesemuanya kekal terang —
                itu keadaan lalai halaman ini. Kad pudar naik terang semula bila
                dituding, supaya jelas ia masih boleh ditekan, bukan dimatikan. */}
            <div className={cx('s-fgrid', pilihan && 's-fgrid--pilih')}>

                {/* Petak PERTAMA dalam grid, saiz SAMA dengan formula rasmi
                    (arahan user 2026-08-09). Bingkai bertitik membezakannya —
                    bahasa yang sama dengan slot avatar kosong: titik bermaksud
                    "belum ada, tekan untuk isi".

                    Ayat penuh reka bentuk asal ("Got an angle that keeps
                    selling? …") tidak muat dalam petak 148px, jadi ia turun ke
                    tooltip. Yang kekal kelihatan ialah dua idea terpenting:
                    boleh cipta sendiri, dan ia milik anda seorang. */}
                <button
                    type="button"
                    className="s-fcard s-fcard--new"
                    onClick={onCipta}
                    title={t('workspace.composer.createOwnTip')}
                >
                    <span className="s-fcard__v">
                        <span className="s-fcard__plus"><Plus size={19} strokeWidth={2} /></span>
                    </span>
                    <span className="s-fcard__n">
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {t('workspace.composer.createOwn')}
                        </span>
                    </span>
                    <span className="s-fcard__d">{t('workspace.composer.createOwnSub')}</span>
                </button>

                {senarai.map((f) => {
                    const on = f.id === pilihan;
                    return (
                        <button
                            key={f.id}
                            type="button"
                            aria-pressed={on}
                            /* Formula terkunci masih DIPAPAR — pelanggan patut nampak
                               apa yang dia belum ada. Ia cuma tidak boleh dipilih;
                               tekanan dihantar ke `onPilih` juga supaya halaman
                               sebenar boleh memaparkan sebabnya (kunci module ada
                               tiga lapis, dan hanya halaman itu yang tahu yang mana
                               satu menghalang). */
                            onClick={() => onPilih(f.id, f)}
                            title={tipKunci(f) || undefined}
                            /* Kelegapan formula terkunci berpindah ke `.s-fcard--kunci`
                               dalam ui.css (2026-08-16). Sebagai gaya inline ia
                               mengalahkan peraturan pudar di atas, jadi kad terkunci
                               akan kekal lebih terang daripada kad biasa yang tidak
                               dipilih — songsang. */
                            className={cx('s-fcard', on && 's-fcard--on', f.terkunci && 's-fcard--kunci')}
                        >
                            <span className="s-fcard__v">
                                {/* Video main sendiri, senyap, berulang — sama
                                    seperti marquee hero salespage. Butang main
                                    DIBUANG: meletakkan ikon main di atas video
                                    yang memang sedang berjalan ialah percanggahan.
                                    Kad tanpa MP4 memapar poster sahaja. */}
                                {f.video
                                    ? <video src={f.video} poster={f.poster} autoPlay muted loop playsInline preload="metadata" />
                                    /* ⛔ JANGAN tambah `loading="lazy"` di sini.
                                       Petak ini `place-items: center`, jadi imej
                                       yang belum dimuat bersaiz 0×0 — dan Chrome
                                       tidak pernah mencetuskan lazy-load untuk
                                       elemen bersaiz sifar. Akibatnya keenam-enam
                                       petak kekal kosong selamanya. Fail ini 13-18KB
                                       sahaja dan berada di atas lipatan; lazy tidak
                                       memberi apa-apa faedah di sini. */
                                    /* Formula tanpa media (cth module yang dicipta
                                       admin) memapar petak kosong, bukan imej rosak. */
                                    : f.poster ? <img src={f.poster} alt="" />
                                        : <span style={{ color: 'var(--s-ink-3)', fontSize: 11 }}>{f.nama}</span>}
                                {f.durasi && <span className="s-fcard__dur">{f.durasi}</span>}

                                {/* Tetapan module — ADMIN sahaja (pengendali hanya
                                    dihantar untuk admin, jadi bukan-admin langsung
                                    tiada butang ini dalam DOM). Membuka modal
                                    tetapan module yang SEDIA ADA: nama, master
                                    prompt, sub-module.

                                    ⛔ Ia SPAN, bukan <button>, dan duduk di dalam
                                       petak media bersama tanda ✓. Dua sebab:
                                       (1) kad ini sendiri sebuah <button>, dan
                                           <button> dalam <button> ialah HTML tidak
                                           sah — penyemak imbas boleh meleraikannya;
                                       (2) cubaan pertama membalut kad dengan <span>
                                           luaran MEMECAHKAN grid: pembalut itu jadi
                                           item grid dan kad kehilangan saiznya
                                           (dilaporkan user 2026-08-12). Tanda ✓ sudah
                                           membuktikan kedudukan mutlak di sini elok. */}
                                {onTetapan && (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        title={t('workspace.composer.moduleSettingsTip')}
                                        onClick={(e) => { e.stopPropagation(); onTetapan(f.id); }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault(); e.stopPropagation(); onTetapan(f.id);
                                            }
                                        }}
                                        style={{
                                            position: 'absolute', top: 6, right: 6,
                                            width: 21, height: 21, borderRadius: 6,
                                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                                            background: 'var(--s-ground)', border: '1px solid var(--s-line-strong)',
                                            color: 'var(--s-ink-2)', cursor: 'pointer',
                                        }}
                                    >
                                        <Pencil size={11} strokeWidth={2} />
                                    </span>
                                )}
                                {f.pil && (
                                    <span style={{
                                        position: 'absolute', bottom: 6, left: 6,
                                        fontSize: 9, fontWeight: 700, letterSpacing: '.04em',
                                        padding: '1px 5px', borderRadius: 4,
                                        background: 'var(--s-ground)', color: 'var(--s-ink-2)',
                                        border: '1px solid var(--s-line-strong)',
                                    }}>{f.pil}</span>
                                )}
                                {f.terkunci && (
                                    <span className="s-fcard__tick" style={{ background: 'var(--s-fill)' }} title={tipKunci(f) || t('workspace.composer.formulaLocked')}>
                                        <Lock size={11} strokeWidth={2.2} />
                                    </span>
                                )}
                                {on && !f.terkunci && (
                                    <span className="s-fcard__tick">
                                        <Check size={12} strokeWidth={3} />
                                    </span>
                                )}
                            </span>
                            <span className="s-fcard__n">
                                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {f.nama}
                                </span>
                                {f.tag && (
                                    <Badge tone="brand" style={{ fontSize: 10, padding: '0 5px', flex: 'none' }}>
                                        {f.tag}
                                    </Badge>
                                )}
                            </span>
                            <span className="s-fcard__d">{notaFormula(f)}</span>
                        </button>
                    );
                })}
            </div>
        </div>
    );
}

/* ---------- Composer -------------------------------------------------- */

/* `nyata` = mod sebenar (halaman /workspace). Null = prototaip, dan
   dalam keadaan itu SATU baris pun tidak berubah daripada sebelum ini.

   ⭐ Kenapa satu fail dua mod, bukan salinan kedua: rupa composer ini
      dibincang berbulan dan masih akan berubah. Dua salinan bermakna
      setiap pelarasan kena dibuat dua kali, dan yang seorang akan
      tertinggal — itu sudah berlaku pada halaman lain dalam projek ini.
      Corak sama seperti SettingsV39View (`modSebenar`).

   ⛔ Yang datang melalui `nyata` ialah DATA dan PENGENDALI sahaja.
      Logik sebenar (cipta produk, tolak kuota, kunci module) kekal di
      App.jsx — fail ini tidak boleh tahu apa-apa tentang Firestore. */
export default function PromptComposerV39({ nyata = null }) {
    const t = useT();

    /* Lampiran menyimpan id LIBRARY — sumber yang SAMA dengan pemilih
       produk di bawah. Dua senarai berasingan bermakna pil dan pemilih
       boleh tidak bersetuju tentang apa yang sudah dilampirkan. */
    /* Lampiran awal diambil daripada My Products, bukan System — kalau
       tidak, ketiga-tiga produk READY dalam tab System sudah dilampirkan
       sebaik halaman dibuka, dan "Pilih semua (0)" membuatkan pemilih
       nampak rosak sedangkan ia berkelakuan betul. */
    const [lampiran, setLampiran] = useState(
        () => LIBRARY.filter((p) => p.sedia && p.sumber === 'own').slice(0, 3).map((p) => p.id),
    );
    const [pilihBuka, setPilihBuka] = useState(false);
    const [kembang, setKembang] = useState(false);
    const [formula, setFormula] = useState('ai8saat');
    const [avatar, setAvatar] = useState('a2');
    const [gaya, setGaya] = useState('auto');

    /* ---- Lapisan penyeragam --------------------------------------------
       Semua JSX di bawah membaca pemboleh ubah ini sahaja, jadi ia tidak
       perlu tahu ia sedang berjalan dalam mod mana. Tanpa lapisan ini,
       setiap baris render akan bercabang `nyata ? … : …` sendiri dan
       kedua-dua mod akan hanyut berasingan. */
    const pil = nyata
        ? nyata.lampiran
        : lampiran.map((id) => {
            const p = LIBRARY.find((x) => x.id === id);
            return { id, nama: p.nama, thumb: p.thumb };
        });
    const buangPil = nyata ? nyata.onBuang : (id) => setLampiran((v) => v.filter((x) => x !== id));
    const bukaPemilih = nyata ? nyata.onTambah : () => setPilihBuka((v) => !v);

    const formulaSenarai = nyata ? nyata.formula : FORMULA;
    const formulaPilihan = nyata ? nyata.formulaPilihan : formula;
    const pilihFormula = nyata ? nyata.onPilihFormula : setFormula;
    /* Mod prototaip membatalkan pilihannya sendiri; mod sebenar TIDAK boleh
       memanggil `onPilihFormula(null)` kerana pengendali itu turut menulis
       `activeModuleId` — satu state yang dikongsi dengan rupa LAMA. Ia
       hanya menurunkan penanda "sudah pilih" milik v3.9. */
    const batalFormula = nyata ? nyata.onBatalFormula : () => setFormula(null);

    const avTetap = nyata ? nyata.avatarTetap : AVATAR_TETAP;
    const avSimpanan = nyata ? nyata.avatarSimpanan : AVATAR_SIMPANAN;
    const avatarPilihan = nyata ? nyata.avatarPilihan : avatar;
    const pilihAvatar = nyata ? nyata.onPilihAvatar : setAvatar;
    const slotAvatar = nyata ? nyata.slotAvatar : SLOT_AVATAR;

    /* Chip Gaya (MODE PRO) DISOROK bila senarainya kosong. Dalam app
       sebenar ia admin-sahaja dan masih mockup — memaparkannya kepada
       pelanggan bermakna satu lagi kawalan yang tidak membuat apa-apa,
       tepat jenis benda yang dibuang daripada halaman Setting 10 Ogos. */
    const gayaSenarai = nyata ? (nyata.gaya || []) : GAYA;
    const gayaPilihan = nyata ? nyata.gayaPilihan : gaya;
    const pilihGaya = nyata ? nyata.onPilihGaya : setGaya;

    const g = gayaSenarai.find((x) => x.id === gayaPilihan) || gayaSenarai[0];
    const a = [...avTetap, ...avSimpanan].find((x) => x.id === avatarPilihan) || avTetap[0];

    /* Nama pilihan terbina datang daripada kamus; nama avatar SIMPANAN
       ialah nama yang pelanggan sendiri taip, jadi ia dipapar apa
       adanya. Sebab itu kunci disemak dahulu, bukan diandaikan ada. */
    const namaAvatar = (x) => (x?.kunci ? t(x.kunci) : x?.nama);
    const namaGaya = (x) => (x?.kunci ? t(x.kunci) : x?.nama);
    const notaGaya = (x) => (x?.kunciNota ? t(x.kunciNota) : x?.nota);

    /* Tiada lagi kotak teks — satu-satunya syarat Generate ialah
       sekurang-kurangnya satu produk dilampirkan.
       Mod sebenar ada syarat tambahan (cth GOYANG wajib ada template),
       jadi keputusannya dibuat di App.jsx dan dihantar siap. */
    const bolehJana = nyata ? nyata.bolehJana : pil.length > 0;

    /* Butang "Produk" membuka pemilih di bawah — bukan menambah terus.
       Dedupe di sini, bukan di dalam pemilih: laluan "guna baru-baru
       ini" boleh menghantar produk yang sudah ada. */
    const tambahDariPemilih = (idBaharu) => {
        setLampiran((v) => [...v, ...idBaharu.filter((id) => !v.includes(id))]);
        setPilihBuka(false);
    };

    return (
        <>
        {/* Fragment: kad composer + kad pemilih ialah DUA kad bersaudara,
            bukan kad dalam kad. Pemilih yang bersarang di dalam composer
            akan mewarisi padding dan bingkainya.

            Kepala kad ("Product & Prompt" + perihal) DIBUANG atas arahan
            user 2026-08-09. Ia diperlukan dahulu untuk membezakan kad ini
            daripada kad Control Panel yang duduk betul-betul di bawahnya;
            sejak kad ini berpindah ke tab Generate sendiri, tab itulah
            yang menamakannya — tajuk kedua cuma mengulang. */}
        <div className="s-panelbox s-panelbox--focus">

            <div className="s-composer" style={{ paddingTop: 16 }}>

                {/* Lampiran produk = pil bernama (keputusan user 2026-08-07,
                    dipilih daripada tiga cadangan).

                    ⛔ JANGAN kembalikan kepada thumbnail tanpa nama. Gambar
                       produk TikTok banyak yang serupa (kotak putih, model
                       sama), jadi thumbnail sahaja tidak memberitahu apa
                       yang sebenarnya dilampirkan.

                    ⭐ Nama dipotong, BUKAN dibalut ke baris kedua — pil yang
                       tingginya berbeza-beza memecahkan barisan. Nama penuh
                       muncul bila dituding. Lebihan dilipat jadi "+N lagi"
                       supaya 30 produk pun tidak menenggelamkan kotak input;
                       memilih 20-30 produk sekali gus memang perkara biasa
                       di sini. */}
                {/* Butang "Produk" duduk di HUJUNG KIRI baris ini, sebelum
                    pil produk (arahan user 2026-08-09). Ia sebaris dengan
                    benda yang ia hasilkan, jadi hubungan "tekan ini →
                    pil muncul di sebelahnya" terbaca terus. Dahulu ia di
                    jalur kaki, jauh daripada kesannya. */}
                <div data-kekal-formula style={{
                    display: 'flex', alignItems: 'center', gap: 6,
                    flexWrap: 'wrap', marginBottom: 12,
                }}>
                    <Chip
                        icon={<Plus size={15} strokeWidth={2} />}
                        on={!nyata && pilihBuka}
                        onClick={bukaPemilih}
                        title={t('workspace.composer.addProductTip')}
                    >
                        {t('workspace.composer.addProduct')}
                    </Chip>

                    {pil.length === 0 && (
                        <span style={{ fontSize: 12.5, color: 'var(--s-ink-3)' }}>
                            {t('workspace.composer.noProductAttached')}
                        </span>
                    )}

                    {(kembang ? pil : pil.slice(0, PIL_TERBUKA)).map((p) => (
                            <span key={p.id} className="s-pil" title={p.nama}>
                                {/* Mod sebenar menghantar URL gambar produk.
                                    Tanpa gambar, petak kekal ISIAN KOSONG —
                                    bukan warna — supaya pil di sini sepadan
                                    dengan petak dalam modal Product Library.
                                    Produk yang sama tidak sepatutnya keluar
                                    merah jambu di satu tempat dan kelabu di
                                    tempat lain. */}
                                {p.img
                                    ? <img className="s-pil__th" src={p.img} alt="" style={{ objectFit: 'cover' }} />
                                    : <span className="s-pil__th" style={{ background: 'var(--s-fill)' }} />}
                                <span className="s-pil__nm">{p.nama}</span>
                                <button
                                    type="button"
                                    className="s-pil__x"
                                    aria-label={t('workspace.composer.removeProduct', { nama: p.nama })}
                                    onClick={() => buangPil(p.id)}
                                >
                                    <X size={12} strokeWidth={2.2} />
                                </button>
                            </span>
                        ))}

                    {pil.length > PIL_TERBUKA && (
                        <button
                            type="button"
                            className="s-pil s-pil--more"
                            onClick={() => setKembang((v) => !v)}
                        >
                            {kembang
                                ? t('workspace.composer.showLess')
                                : t('workspace.composer.showMore', { n: pil.length - PIL_TERBUKA })}
                        </button>
                    )}
                </div>

                {/* Galeri formula duduk DI DALAM kad ini (arahan user
                    2026-08-09), bukan sebagai kad berasingan di bawah —
                    memilih formula ialah sebahagian daripada menyediakan
                    janaan, bukan kerja yang berlainan. */}
                <GaleriFormula
                    pilihan={formulaPilihan}
                    onPilih={pilihFormula}
                    onBatal={batalFormula}
                    senarai={formulaSenarai}
                    contoh={!nyata}
                    onCipta={nyata?.onCiptaFormula}
                    onTetapan={nyata?.onTetapanFormula}
                    onCiptaModule={nyata?.onCiptaModule}
                />

                {/* Kotak yang muncul ikut formula (template GOYANG, sub-module,
                    Suara Watak FLOWNITY). Ia dihantar SIAP dari App.jsx —
                    logiknya tidak ditulis semula di sini, cuma dipindahkan
                    tempatnya. Prototaip tidak menghantar apa-apa. */}
                {/* Jalur tetapan ikut formula — DILINDUNGI daripada batal-bila-
                    tekan-luar: ia hanya wujud kerana ada formula dipilih, jadi
                    menekannya tidak boleh membuang formula itu. */}
                {nyata?.tambahan && <div data-kekal-formula>{nyata.tambahan}</div>}
            </div>

            {/* Jalur kaki kad: tetapan di kiri, tindakan utama di kanan —
                bentuk yang sama dengan "Asia/Kuala_Lumpur … Edit
                availability" pada kad Availability Cal.

                `nowrap` SENGAJA — baris ini mesti kekal satu baris.
                Kalau kandungan baharu ditambah kemudian, kecilkan
                sesuatu yang lain; jangan biarkan ia membalut. */}
            <div className="s-cardfoot" data-kekal-formula style={{ gap: 5, flexWrap: 'nowrap' }}>

                    {/* Chip "Produk" DIPINDAHKAN ke atas baris pil produk
                        (arahan user 2026-08-09). Ia tidak lagi di sini. */}

                    {/* Chip Formula DIBUANG. Galeri formula kini duduk dalam
                        kad yang SAMA, beberapa inci di atas baris ini —
                        dua kawalan untuk nilai yang sama, dalam satu
                        pandangan, hanya membuatkan pengguna tertanya yang
                        mana satu betul. Galeri menang: ia menunjukkan
                        rupa video, chip cuma menunjukkan nama. */}

                    {/* Chip avatar memaparkan MUKA yang sedang dipilih, bukan
                        sekadar perkataan — pengguna tahu sekali pandang siapa
                        yang akan muncul dalam video. */}
                    {/* Chip memaparkan muka yang sedang dipilih — pengguna
                        tahu sekali pandang siapa akan muncul dalam video. */}
                    {/* Formula mod tangan (GOYANG) tiada watak langsung, jadi
                        pemilih avatar diganti dengan makluman — bukan sekadar
                        dimalapkan. Pemilih yang boleh ditekan tetapi tidak
                        memberi kesan lebih mengelirukan daripada tiada
                        pemilih: pelanggan akan memilih muka, kemudian
                        tertanya kenapa ia tiada dalam video. */}
                    {nyata?.avatarMati ? (
                        <span className="s-chip" style={{ cursor: 'default', color: 'var(--s-ink-3)' }} title={nyata.avatarMatiNota}>
                            <UserX size={14} strokeWidth={1.8} />
                            {nyata.avatarMatiNota}
                        </span>
                    ) : (
                    <ChipMenu
                        icon={<Muka av={a} s />}
                        label={avatarPilihan === 'none' ? t('workspace.composer.avatar') : namaAvatar(a)}
                        lebar={252}
                    >
                        {avTetap.map((x) => (
                            <BarisPop
                                key={x.id}
                                tanda={<Muka av={x} pilih={x.id === avatarPilihan} />}
                                nama={namaAvatar(x)}
                                dipilih={x.id === avatarPilihan}
                                onClick={() => pilihAvatar(x.id)}
                            />
                        ))}

                        {avSimpanan.length > 0 && <span className="s-pop__sep" />}

                        {avSimpanan.map((x) => (
                            <BarisPop
                                key={x.id}
                                tanda={<Muka av={x} pilih={x.id === avatarPilihan} />}
                                nama={x.nama}
                                nota={[x.gender, x.umur].filter(Boolean).join(' · ') || undefined}
                                dipilih={x.id === avatarPilihan}
                                onClick={() => pilihAvatar(x.id)}
                                onSunting={nyata ? () => nyata.onSuntingAvatar?.(x.id) : () => { }}
                            />
                        ))}

                        <span className="s-pop__sep" />

                        <BarisPop
                            tanda={<Muka av={{ tambah: true }} />}
                            nama={<span style={{ color: 'var(--s-ink-2)' }}>{t('workspace.composer.newAvatar')}</span>}
                            hujung={`${avSimpanan.length}/${slotAvatar}`}
                            onClick={nyata ? () => nyata.onAvatarBaharu?.() : () => { }}
                        />
                    </ChipMenu>
                    )}

                    {/* Mod sebenar: SUIS, bukan menu (arahan user 2026-08-12).
                        Mati secara lalai. Prototaip kekal menu dua pilihan. */}
                    {nyata ? (nyata.proMode !== null && nyata.proMode !== undefined && (
                        <>
                        <Chip
                            icon={<Wand2 size={14} strokeWidth={1.9} />}
                            on={nyata.proMode}
                            onClick={() => nyata.onProMode(!nyata.proMode)}
                            title={nyata.proMode
                                ? t('workspace.composer.proModeOnTip')
                                : t('workspace.composer.proModeOffTip')}
                        >
                            {/* Label dan suis dibungkus SATU anak inline-flex.
                                Sebagai dua anak berasingan ia terkeluar dari
                                baris chip (dilaporkan user 2026-08-12) — kotak
                                tanda jatuh ke bawah teks dan tersembul keluar
                                bingkai chip.

                                Bentuknya suis, bukan kotak tanda: kotak tanda
                                bermaksud "pilih satu daripada beberapa", suis
                                bermaksud hidup/mati — dan itulah yang Pro Mode. */}
                            <span style={{
                                display: 'inline-flex', alignItems: 'center', gap: 7,
                                whiteSpace: 'nowrap', lineHeight: 1,
                            }}>
                                {t('workspace.composer.proMode')}
                                <span style={{
                                    width: 26, height: 15, flex: 'none', borderRadius: 999,
                                    display: 'inline-flex', alignItems: 'center',
                                    padding: 2, boxSizing: 'border-box',
                                    justifyContent: nyata.proMode ? 'flex-end' : 'flex-start',
                                    background: nyata.proMode ? 'var(--s-select)' : 'transparent',
                                    border: `1px solid ${nyata.proMode ? 'var(--s-select)' : 'var(--s-line-strong)'}`,
                                    transition: 'all .15s',
                                }}>
                                    <span style={{
                                        width: 11, height: 11, borderRadius: '50%',
                                        background: nyata.proMode ? 'var(--s-select-ink)' : 'var(--s-ink-3)',
                                    }} />
                                </span>
                            </span>
                        </Chip>
                        {/* Baris status Pro Mode (PLAN_PROSTUDIO_BLOCKS.md bahagian 2):
                            "GOYANG · N blocks", atau ajakan membuka PRO Studio bila
                            tiada blocks tersimpan. Butang (bukan <a>) supaya navigasi
                            kekal SPA — muat semula penuh akan membuang produk yang
                            sedang di-stage dalam composer. */}
                        {nyata.proModeNota && (
                            <button
                                type="button"
                                onClick={nyata.onProModeNota || undefined}
                                className="s-dim"
                                style={{
                                    background: 'none', border: 0, padding: 0, fontFamily: 'inherit',
                                    fontSize: 12, whiteSpace: 'nowrap',
                                    cursor: nyata.onProModeNota ? 'pointer' : 'default',
                                    textDecoration: nyata.onProModeNota ? 'underline' : 'none',
                                }}
                            >
                                {nyata.proModeNota}
                            </button>
                        )}
                        </>
                    )) : gayaSenarai.length > 0 && (
                        <ChipMenu icon={<Wand2 size={14} strokeWidth={1.9} />} label={namaGaya(g)} lebar={290}>
                            {gayaSenarai.map((x) => (
                                <BarisPop
                                    key={x.id}
                                    nama={namaGaya(x)}
                                    nota={notaGaya(x)}
                                    dipilih={x.id === gayaPilihan}
                                    onClick={() => pilihGaya(x.id)}
                                />
                            ))}
                        </ChipMenu>
                    )}

                    {/* Chip "N set" DIBUANG (arahan user 2026-08-07): tiada
                        logik di sebaliknya dalam prototaip ini, dan ia menolak
                        baris chip pecah dua baris. Bilangan set ditentukan oleh
                        formula yang dipilih — AI8SAAT tiga set, FLOWNITY lima
                        scene satu video — jadi ia bukan tetapan berasingan.
                        Kalau ia perlu dikembalikan nanti, letak di dalam menu
                        chip Formula, bukan sebagai chip sendiri. */}

                    {/* Ikon pelaras yang membuka Panel larian DIBUANG —
                        panel itu kini duduk terus di bawah composer dalam
                        lajur Control Panel yang sama. Pintasan ke benda
                        yang sudah kelihatan hanya menambah kekeliruan. */}

                    {/* Bulk Import — di sebelah KANAN suis Pro Mode (arahan
                        user 2026-08-12). Admin + Smart Custom sahaja; syarat
                        itu ditentukan di App.jsx, jadi bagi pengguna lain
                        pengendalinya null dan chip ini langsung tidak wujud. */}
                    {nyata?.onBulkImport && (
                        <Chip
                            icon={<Upload size={14} strokeWidth={1.9} />}
                            onClick={nyata.onBulkImport}
                            title={t('workspace.composer.bulkImportTip')}
                        >
                            {t('workspace.composer.bulkImport')}
                        </Chip>
                    )}

                    <span style={{ marginLeft: 'auto' }} />

                    {/* Kuota dimasukkan KE DALAM butang Generate (keputusan user
                        2026-08-07). Sebabnya: kuota hanya bermakna pada saat kau
                        hendak menekan Generate — sebagai lencana berasingan ia
                        cuma satu lagi nombor pada baris yang sudah sesak.

                        ⛔ BAKI HARIAN SAHAJA. Kos tekanan itu (cth "3 ·") pernah
                           dipapar di sini dan DIBUANG atas arahan user: dua nombor
                           bersebelahan dalam satu butang kecil menimbulkan soalan
                           "yang mana satu?" pada saat pengguna sepatutnya sekadar
                           menekan. Kos per tekanan tetap ada, dieja penuh pada
                           baris kiraan di bawah composer. */}
                    {/* Ikon kilauan DIBUANG daripada butang ini: ia memakan
                        ~20px sedangkan baris ini terlebih 27px. Perkataan
                        "Generate" sudah cukup jelas tanpa ikon; lencana kuota
                        di dalamnya lebih berharga daripada hiasan. */}
                    {/* Pembalut ref: popup ajakan Upgrade mengukur kedudukan
                        butang ini untuk meletakkan dirinya. Ia dibalut, bukan
                        diberi terus kepada <Btn>, kerana Btn tidak menghantar
                        ref ke nod DOM — memberi ref kepadanya menghasilkan
                        rujukan kosong dan popup mendarat di sudut skrin. */}
                    <span
                        ref={nyata?.janaRef}
                        className={nyata?.janaDenyut ? 'animate-pulse' : undefined}
                        style={{ display: 'inline-flex', flex: 'none', borderRadius: 10 }}
                    >
                    <Btn
                        size="sm"
                        style={{ flex: 'none' }}
                        disabled={!bolehJana}
                        onClick={nyata?.onGenerate}
                        title={nyata
                            ? (nyata.tipJana || '')
                            : (bolehJana
                                ? t('workspace.composer.generateTip')
                                : t('workspace.composer.generateTipNeedProduct'))}
                    >
                        {nyata?.labelJana || t('workspace.composer.generate')}
                        {/* Kuota disorok bila tiada nilai — pelanggan lama yang
                            tiernya tidak ter-set langsung tiada kaunter, dan
                            lencana kosong lebih mengelirukan daripada tiada. */}
                        {(nyata ? nyata.kuota : '12/100') && (
                            <span className="s-btn__kuota">
                                <Zap size={11} strokeWidth={2.4} />
                                {nyata ? nyata.kuota : '12/100'}
                            </span>
                        )}
                    </Btn>
                    </span>
            </div>

            {/* Baris kiraan ("N produk · N video · N kuota") dan pautan
                "Lihat semua formula" DIBUANG (arahan user 2026-08-07).
                Kiraan itu mengulang apa yang pil produk sudah tunjukkan, dan
                pautan formula sudah ada di menu tepi — dua-duanya menambah
                baris tanpa menambah maklumat. */}
        </div>

        {/* Pemilih inline ialah barang PROTOTAIP. Mod sebenar membuka modal
            Product Library yang sedia ada (keputusan user 2026-08-12) —
            modal itu sudah membawa tab System/My Products, carian, group,
            badge READY/PENDING dan kunci produk PENDING, semuanya teruji
            dan lulus rules. Membina semula di sini bermakna menyalin
            kunci PENDING; kalau salinan itu tersasar, pelanggan boleh
            melampirkan produk tanpa data dan Generate akan gagal selepas
            dia menunggu. */}
        {/* Kad pemilih produk (mod prototaip) — DILINDUNGI daripada
            batal-bila-tekan-luar. Ia kad BERSAUDARA, bukan di dalam kad
            composer, jadi tanpa penanda ini memilih produk di sini akan
            membuang formula yang baru dipilih. Mod SEBENAR membuka modal
            (kelas s-modal), yang sudah dilindungi sebagai lapisan terapung. */}
        {!nyata && pilihBuka && (
            <div data-kekal-formula>
                <PemilihProduk
                    dilampirkan={lampiran}
                    onTambah={tambahDariPemilih}
                    onTutup={() => setPilihBuka(false)}
                />
            </div>
        )}

        </>
    );
}
